/**
 * Auth ⇆ api-client bridge (03-05 §4).
 *
 * Turns a `TokenStore` + a refresh call into the two hooks `customInstance`
 * needs: an `Authorization` provider and the single 401 policy. This is the ONE
 * place both apps get their 401 handling — register once at bootstrap.
 */

import { type HttpHooks, registerHttpHooks } from '@smartsash/api-client';

import { createRefreshCoordinator } from './refresh';
import type { AuthTokens, TokenStore } from './tokens';

export interface AuthHttpHooksConfig {
  tokenStore: TokenStore;
  /**
   * Perform the refresh network call. Resolve with new tokens (bearer) or `null`
   * (cookie mode — the browser refreshed via cookie); throw on failure. Must send
   * `{ skipAuthRefresh: true }` so a 401 from the refresh endpoint does not recurse.
   */
  refresh(): Promise<AuthTokens | null>;
  /** Invoked when a refresh is denied (session gone) so the app can log out. */
  onSessionExpired(): void;
  /**
   * Re-read the session's permission snapshot after a 403 (HS-1387). `createAuth` supplies
   * `client.refreshSession`; omit it and the 403 policy only reports.
   *
   * A stale snapshot is the likeliest reason a 403 surprises the UI at all — the client showed a
   * control the server has since revoked — so re-reading is what stops the same refusal from
   * being offered again. It must be single-flight; `refreshSession` is.
   */
  refreshSession?(): Promise<unknown>;
  /**
   * Report a 403 centrally (HS-1387). Packages here own no logger, so the sink is the app's.
   *
   * Without it the only trace of a 403 is whatever the screen that made the request chose to
   * render, which is exactly the "no single place to see it" the audit found.
   */
  reportForbidden?(ctx: { status: number; url?: string }): void;
}

export type AuthHttpHooks = Pick<HttpHooks, 'getAuthHeader' | 'onUnauthorized' | 'onForbidden'>;

/**
 * Build the `{ getAuthHeader, onUnauthorized }` pair.
 *
 * `getAuthHeader` emits a bearer token only in bearer mode (cookie mode relies
 * on the browser's cookie). `onUnauthorized` runs one shared refresh for a 401
 * burst, then retries the original request — so N concurrent 401s trigger
 * exactly one refresh call.
 */
export function createAuthHttpHooks(config: AuthHttpHooksConfig): AuthHttpHooks {
  const { tokenStore } = config;

  const coordinator = createRefreshCoordinator({
    performRefresh: async () => {
      const tokens = await config.refresh();
      // Persist the fresh access token BEFORE any waiting request retries, so the
      // retry sends the new bearer header. Cookie mode returns null (no-op).
      if (tokens) tokenStore.setTokens(tokens);
    },
    onSessionExpired: () => {
      config.onSessionExpired();
    },
  });

  return {
    getAuthHeader: () => {
      if (tokenStore.mode !== 'bearer') return null;
      const token = tokenStore.getAccessToken();
      return token ? `Bearer ${token}` : null;
    },
    onUnauthorized: async ({ retry }) => {
      await coordinator.refresh();
      return retry();
    },
    /**
     * The 403 policy (HS-1387). Deliberately NOT a retry: unlike a 401 there is no credential to
     * renew, so a retry would double every genuinely refused request. It reports, and re-reads
     * the snapshot so the UI stops offering what the server just refused. The request itself
     * still rejects with its own error.
     *
     * `refreshSession` is single-flight, so the concurrent 403s a screen produces collapse into
     * one read. Sequential refusals do each trigger one — which is the honest behaviour, since
     * by then the answer may genuinely have changed.
     */
    onForbidden: (ctx) => {
      config.reportForbidden?.(ctx);
      // Fire-and-forget: the caller is an error path that must not wait on this, and a failed
      // re-read simply leaves the previous snapshot in place.
      void config.refreshSession?.().catch(() => undefined);
    },
  };
}

/** Build the hooks and register them into the shared api-client instance. */
export function installAuthHttpHooks(config: AuthHttpHooksConfig): AuthHttpHooks {
  const hooks = createAuthHttpHooks(config);
  registerHttpHooks(hooks);
  return hooks;
}
